import { useState } from "react";
import { format } from "date-fns";
import { Importer, ImporterField, deDE } from "react-csv-importer";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { toast } from "react-toastify";

import "react-csv-importer/dist/index.css";

import kidsApi from "../../api/kids.api";
import { mapKidRowToModel } from "./mapRowToModel";

import Loader from "@components/Loader";

import stores from "@stores";

const waitFor = (ms) =>
  new Promise((resolve, _reject) => {
    setTimeout(resolve, ms);
  });

const useKidsImport = ({ projectId, onImportCompleted = () => ({}) }) => {
  const { studentsStore } = stores;
  const [showImportBar, setShowImportBar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const processRow = async (row) => {
    try {
      const mappedKid = mapKidRowToModel(row);

      if (mappedKid.address !== "") {
        const [geocode] = await geocodeByAddress(mappedKid.address);
        const { lat, lng } = await getLatLng(geocode);

        mappedKid.coordinates = [lng, lat];
        mappedKid.placeId = geocode.place_id;
        // Artificially make a pause, to not exceed google api usage limits.
        await waitFor(1000);

        if (mappedKid.entranceOrdinary) {
          setLoading(true);
          await kidsApi.add({
            ...mappedKid,
            birthday: mappedKid?.birthday
              ? format(new Date(mappedKid?.birthday), "dd. MMMM yyyy")
              : "",
            project: projectId,
          });
          await studentsStore.fetchKidsList(projectId);
        }
      } else {
        toast.error(
          `Unable to import following student, address is empty: ${row}`
        );
      }
      setLoading(false);
    } catch (error) {
      setErrors([...errors, error]);
      toast.error(`Error happened with parsing the row ${error}`);
    }
  };

  const ImportView = showImportBar ? (
    <>
      {loading && <Loader filled />}
      <Importer
        dataHandler={async (rows, { _startIndex }) => {
          for (const row of rows) {
            await processRow(row);
          }
        }}
        locale={deDE}
        defaultNoHeader={false}
        restartable={false}
        onComplete={({ file, preview, fields, columnFields }) => {
          onImportCompleted();
        }}
        onClose={({ file, preview, fields, columnFields }) => {
          setShowImportBar(false);
        }}
        skipEmptyLines
      >
        <ImporterField name="susId" label="SuS_ID" />
        <ImporterField name="lastName" label="Name" />
        <ImporterField name="firstName" label="Vorname" />
        <ImporterField name="address" label="Adresse" optional />
        <ImporterField name="postalCode" label="PLZ" optional />
        <ImporterField name="city" label="Stadt" optional />
        <ImporterField name="gender" label="Geschlecht" />
        <ImporterField name="teacherName" label="LP" optional />
        <ImporterField name="germanLanguageLevel" label="Deutsch" />
        <ImporterField name="motherLanguage" label="Umgangssprache" />
        <ImporterField name="previousExperience" label="Erfahrung Betreuung" />
        <ImporterField name="entranceOrdinary" label="Eintritt ordentlich" />
        <ImporterField name="note" label="Notizen" optional />
        <ImporterField name="specialNote" label="Wunsch" optional />
        <ImporterField name="schoolBus" label="Schulbus" optional />
        <ImporterField name="birthday" label="Geburtstag" optional />
      </Importer>
    </>
  ) : (
    ""
  );

  return {
    openImport: () => setShowImportBar(true),
    closeImport: () => setShowImportBar(false),
    isImportActive: showImportBar,
    ImportView,
  };
};

export default useKidsImport;
